import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CreditCard, FileText, Download, CheckCircle, CheckCircle2, XCircle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { SignaturePad } from '../../components/ui/signature-pad';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_KYC_DOCUMENT_TYPES = [
  { value: 'identity_front', label: "Pièce d'identité — Recto", group: 'identity' },
  { value: 'identity_back', label: "Pièce d'identité — Verso", group: 'identity' },
  { value: 'proof_address', label: 'Justificatif de domicile' },
  { value: 'proof_income', label: 'Justificatif de revenus' },
  { value: 'bank_statement', label: 'Relevé bancaire' },
  { value: 'iban', label: 'IBAN / RIB' },
  { value: 'tax_return', label: "Avis d'imposition" },
  { value: 'selfie', label: 'Selfie avec pièce' },
  { value: 'other', label: 'Autre document' },
];

// Helper to get headers with mirror mode support
const getHeaders = (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  const mirrorClientId = localStorage.getItem('mirrorClientId');
  if (mirrorClientId) {
    headers['X-Mirror-Client-Id'] = mirrorClientId;
  }
  return headers;
};

export default function ClientDocuments() {
  const { token, user } = useAuth();
  const { t } = useLanguage();
  const [ibans, setIbans] = useState([]);
  const [kycDocs, setKycDocs] = useState([]);
  const [kycRequiredDocs, setKycRequiredDocs] = useState([]);
  const [statements, setStatements] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [mandates, setMandates] = useState([]);
  const [signAdvance, setSignAdvance] = useState(null);
  const [signMandate, setSignMandate] = useState(null);
  const [signCity, setSignCity] = useState('');
  const [signAccord, setSignAccord] = useState(false);
  const [signTerms, setSignTerms] = useState(false);
  const signPadRef = useRef(null);
  const [signing, setSigning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [uploadingKyc, setUploadingKyc] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token, user]);

  const fetchData = async () => {
    try {
      const [ibanRes, kycRes, kycReqRes, advancesRes, mandatesRes] = await Promise.all([
        axios.get(`${API}/iban-requests/my`, {
          headers: getHeaders(token)
        }).catch(() => ({ data: { ibans: [] } })),
        axios.get(`${API}/kyc/${user?.id}`, {
          headers: getHeaders(token)
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/kyc-requirements/${user?.id}`, {
          headers: getHeaders(token)
        }).catch(() => ({ data: { documents: [] } })),
        axios.get(`${API}/advances`, {
          headers: getHeaders(token)
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/mandates`, {
          headers: getHeaders(token)
        }).catch(() => ({ data: [] }))
      ]);
      setIbans(ibanRes.data.ibans || []);
      setKycDocs(Array.isArray(kycRes.data) ? kycRes.data : []);
      setKycRequiredDocs(kycReqRes.data?.documents || []);
      setAdvances(advancesRes.data || []);
      setMandates(mandatesRes.data || []);
      // Filter out account statements from KYC docs
      const allDocs = Array.isArray(kycRes.data) ? kycRes.data : [];
      const stmts = allDocs.filter(d => d.document_type === 'account_statement');
      setStatements(stmts);
      setKycDocs(allDocs.filter(d => d.document_type !== 'account_statement'));
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadIbanPdf = async (iban) => {
    setDownloadingPdf(iban.id);
    try {
      const response = await axios.get(`${API}/iban-requests/${iban.id}/pdf`, {
        headers: getHeaders(token),
        responseType: 'blob',
        timeout: 30000,
      });
      
      // Check if response is actually a PDF (not an error JSON returned as blob)
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('application/json')) {
        // Server returned JSON error wrapped in blob
        const text = await response.data.text();
        const err = JSON.parse(text);
        throw new Error(err.detail || 'Erreur serveur');
      }
      
      if (response.data.size === 0) {
        throw new Error('Fichier vide');
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IBAN-${iban.iban?.slice(-8) || 'document'}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('IBAN téléchargé');
    } catch (error) {
      console.error('Download error:', error);
      // Try to extract meaningful error from blob response
      let msg = 'Erreur lors du téléchargement';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch { /* ignore parse errors */ }
      } else if (error.response?.data?.detail) {
        msg = error.response.data.detail;
      } else if (error.message && error.message !== 'Fichier vide') {
        msg = error.message;
      }
      toast.error(msg);
      
      // Fallback: try opening in a new tab with token
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        try {
          window.open(`${API}/iban-requests/${iban.id}/pdf?token=${token}`, '_blank');
        } catch { /* ignore */ }
      }
    } finally {
      setDownloadingPdf(null);
    }
  };

  const getIbanStatusBadge = (status) => {
    const config = {
      pending: { variant: 'secondary', icon: Clock, label: 'En attente', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
      validated: { variant: 'outline', icon: CheckCircle, label: 'Validé', className: 'bg-green-100 text-green-800 border-green-300' },
      expired: { variant: 'outline', icon: AlertCircle, label: 'Expiré', className: 'bg-orange-100 text-orange-800 border-orange-300' },
      deleted: { variant: 'outline', icon: XCircle, label: 'Supprimé', className: 'bg-red-100 text-red-800 border-red-300' }
    };
    const { icon: Icon, label, className } = config[status] || config.pending;
    return (
      <Badge variant="outline" className={className}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  const getKycStatusBadge = (status) => {
    const config = {
      pending: { variant: 'secondary', icon: Clock, label: 'En attente' },
      under_review: { variant: 'secondary', icon: Clock, label: 'En révision' },
      approved: { variant: 'default', icon: CheckCircle, label: 'Validé' },
      rejected: { variant: 'destructive', icon: XCircle, label: 'Refusé' }
    };
    const { variant, icon: Icon, label } = config[status] || config.pending;
    return (
      <Badge variant={variant}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  // Format IBAN with spaces
  const formatIban = (iban) => {
    if (!iban) return '';
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Mes Documents
          </h1>
          <p className="text-muted-foreground">Gérez vos coordonnées bancaires et documents</p>
        </div>

        {/* IBAN Section */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <CreditCard className="w-5 h-5 text-[#D4AF37]" />
            Coordonnées Bancaires
          </h2>
          
          {ibans.length === 0 ? (
            <Card className="card-premium">
              <CardContent className="py-8 text-center">
                <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Aucune coordonnée bancaire</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Effectuez une demande de dépôt pour obtenir un IBAN
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {ibans.map(iban => (
                <Card key={iban.id} className="card-premium" data-testid={`iban-${iban.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                        Coordonnées bancaires
                      </CardTitle>
                      {getIbanStatusBadge(iban.status)}
                    </div>
                    {iban.status === 'pending' && (
                      <CardDescription className="text-yellow-600">
                        Votre demande est en cours de traitement
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {iban.status === 'validated' ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Bénéficiaire</p>
                            <p className="font-semibold">{iban.beneficiary_name}</p>
                          </div>
                          {iban.bank_name && (
                            <div>
                              <p className="text-xs text-muted-foreground uppercase tracking-wide">Banque</p>
                              <p className="font-semibold">{iban.bank_name}</p>
                            </div>
                          )}
                        </div>
                        
                        {(iban.address || iban.postal_code || iban.city) && (
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Adresse</p>
                            <p className="font-semibold">
                              {iban.address && <span>{iban.address}<br/></span>}
                              {iban.postal_code} {iban.city}
                            </p>
                          </div>
                        )}
                        
                        <div className="p-4 bg-secondary/30 rounded-lg">
                          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">IBAN</p>
                          <p className="font-mono text-lg font-bold text-[#D4AF37]">
                            {formatIban(iban.iban)}
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">BIC / SWIFT</p>
                            <p className="font-mono font-semibold">{iban.swift}</p>
                          </div>
                          {iban.transfer_reference && (
                            <div className="col-span-1 md:col-span-2 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                              <p className="text-xs text-blue-400 uppercase tracking-wide font-semibold">Référence obligatoire du virement</p>
                              <p className="text-xs text-muted-foreground mb-1">À indiquer impérativement dans le motif du virement</p>
                              <p className="font-mono font-bold text-lg text-blue-400">{iban.transfer_reference}</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex justify-end pt-2">
                          <Button 
                            onClick={() => downloadIbanPdf(iban)}
                            disabled={downloadingPdf === iban.id}
                            data-testid={`download-iban-${iban.id}`}
                          >
                            {downloadingPdf === iban.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 mr-2" />
                            )}
                            Télécharger PDF
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <Clock className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
                        <p className="text-muted-foreground">
                          {iban.status === 'pending' 
                            ? 'En attente d\'attribution par votre gestionnaire'
                            : iban.status === 'expired' 
                              ? 'Cet IBAN a expiré'
                              : 'IBAN non disponible'
                          }
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Demande créée le {new Date(iban.created_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Account Statements */}
        {statements.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              <FileText className="w-5 h-5 text-[#D4AF37]" />
              Relevés de compte
            </h2>
            <div className="space-y-3">
              {statements.map(stmt => (
                <Card key={stmt.id} className="card-premium">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{stmt.label || 'Relevé de compte'}</p>
                        <p className="text-xs text-muted-foreground">
                          Partagé le {stmt.uploaded_at ? new Date(stmt.uploaded_at).toLocaleDateString('fr-FR') : ''}
                        </p>
                      </div>
                    </div>
                    {stmt.file_data && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const a = document.createElement('a');
                          a.href = stmt.file_data;
                          a.download = stmt.file_name || 'releve-de-compte.pdf';
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                        }}
                        data-testid={`download-statement-${stmt.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Télécharger
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Avances de Fonds */}
        {advances.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" /> Avances de Fonds
            </h2>
            {advances.map(adv => {
              const statusLabels = { pending_signature: 'En attente de signature', signed: 'Signée - en cours de validation', active: 'Active', repaid: 'Remboursée', cancelled: 'Annulée' };
              const statusColors = { pending_signature: 'bg-yellow-500/20 text-yellow-400', signed: 'bg-blue-500/20 text-blue-400', active: 'bg-emerald-500/20 text-emerald-400', repaid: 'bg-gray-500/20 text-gray-400', cancelled: 'bg-red-500/20 text-red-400' };
              return (
                <Card key={adv.id} className="bg-card/80 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-bold">{adv.amount?.toLocaleString('fr-FR')} €</p>
                        <p className="text-xs text-muted-foreground">Avance du {adv.date} · Ref: AVF-{adv.id?.slice(0, 8).toUpperCase()}</p>
                        {adv.due_date && <p className="text-xs text-muted-foreground">Échéance: {adv.due_date}</p>}
                        {adv.status === 'active' && <p className="text-xs text-muted-foreground">Reste: {adv.remaining?.toLocaleString('fr-FR')} €</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[adv.status] || ''}>{statusLabels[adv.status] || adv.status}</Badge>
                        <Button size="sm" variant="outline" onClick={() => window.open(`${API}/advances/${adv.id}/contract?token=${token}`, '_blank')}>Contrat</Button>
                        {adv.status === 'pending_signature' && (
                          <Button size="sm" onClick={() => { setSignAdvance(adv); setSignCity(''); setSignAccord(false); setSignTerms(false); }}>Signer</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Mandats de Gestion */}
        {mandates.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Mandats de Gestion</h2>
            {mandates.map(m => {
              const statusLabels = { pending_signature: 'En attente de signature', signed: 'Signé - en cours de validation', active: 'Actif', cancelled: 'Annulé' };
              const statusColors = { pending_signature: 'bg-yellow-500/20 text-yellow-400', signed: 'bg-blue-500/20 text-blue-400', active: 'bg-emerald-500/20 text-emerald-400', cancelled: 'bg-red-500/20 text-red-400' };
              return (
                <Card key={m.id} className="bg-card/80 border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-lg font-bold">Mandat de Gestion</p>
                        <p className="text-xs text-muted-foreground">Ref: MDG-{m.id?.slice(0,8).toUpperCase()} · {m.date}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[m.status] || ''}>{statusLabels[m.status] || m.status}</Badge>
                        <Button size="sm" variant="outline" onClick={() => window.open(`${API}/mandates/${m.id}/contract?token=${token}`, '_blank')}>Contrat</Button>
                        {m.status === 'pending_signature' && (
                          <Button size="sm" onClick={() => { setSignMandate(m); setSignCity(''); setSignAccord(false); setSignTerms(false); }}>Signer</Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* KYC Documents */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            Documents KYC
          </h2>
          
          {(() => {
            const availableTypes = (kycRequiredDocs && kycRequiredDocs.length > 0)
              ? ALL_KYC_DOCUMENT_TYPES.filter(dt => kycRequiredDocs.some(r => r.type === dt.value))
              : [];
            
            if (availableTypes.length === 0) {
              return (
                <Card className="card-premium">
                  <CardContent className="py-8 text-center">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="font-medium text-green-500">Aucun document requis</p>
                    <p className="text-sm text-muted-foreground">Votre conseiller n'a demandé aucun document pour le moment</p>
                  </CardContent>
                </Card>
              );
            }

            const allApproved = availableTypes.every(t => 
              kycDocs.some(d => d.document_type === t.value && d.status === 'approved')
            );

            return (
              <div className="space-y-3">
                {allApproved && (
                  <Card className="card-premium border-green-500/30 bg-green-500/5">
                    <CardContent className="py-4 text-center">
                      <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-green-500" />
                      <p className="font-medium text-green-500">Profil vérifié — Tous les documents approuvés</p>
                    </CardContent>
                  </Card>
                )}
                {(() => {
                  // Group identity_front and identity_back together
                  const identityTypes = availableTypes.filter(t => t.group === 'identity' || t.value === 'identity_front' || t.value === 'identity_back');
                  const otherTypes = availableTypes.filter(t => t.group !== 'identity' && t.value !== 'identity_front' && t.value !== 'identity_back');
                  const elements = [];

                  // Render identity group as a single card with recto/verso
                  if (identityTypes.length > 0) {
                    const frontType = identityTypes.find(t => t.value === 'identity_front');
                    const backType = identityTypes.find(t => t.value === 'identity_back');
                    const frontUploaded = kycDocs.find(d => d.document_type === 'identity_front');
                    const backUploaded = kycDocs.find(d => d.document_type === 'identity_back');
                    const bothApproved = frontUploaded?.status === 'approved' && backUploaded?.status === 'approved';
                    const anyRejected = frontUploaded?.status === 'rejected' || backUploaded?.status === 'rejected';

                    elements.push(
                      <Card key="identity-group" className={`card-premium ${bothApproved ? 'border-green-500/30' : anyRejected ? 'border-red-500/30' : 'border-border/50'}`} data-testid="kyc-identity-group">
                        <CardContent className="p-4 space-y-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${bothApproved ? 'bg-green-500/20' : 'bg-blue-500/20'}`}>
                              <FileText className={`w-5 h-5 ${bothApproved ? 'text-green-500' : 'text-blue-500'}`} />
                            </div>
                            <div>
                              <p className="font-semibold">Pièce d'identité (CNI / Passeport)</p>
                              <p className="text-xs text-muted-foreground">Veuillez fournir le recto et le verso</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {[{ type: frontType, uploaded: frontUploaded, label: 'Recto', testId: 'kyc-identity-front' },
                              { type: backType, uploaded: backUploaded, label: 'Verso', testId: 'kyc-identity-back' }]
                              .filter(item => item.type)
                              .map(({ type, uploaded, label, testId }) => {
                                const isApproved = uploaded?.status === 'approved';
                                const isRejected = uploaded?.status === 'rejected';
                                const isPending = uploaded && !isApproved && !isRejected;
                                const isMissing = !uploaded;

                                return (
                                  <div key={type.value} className={`p-3 rounded-lg border ${isApproved ? 'border-green-500/30 bg-green-500/5' : isRejected ? 'border-red-500/30 bg-red-500/5' : isPending ? 'border-yellow-500/30 bg-yellow-500/5' : 'border-dashed border-border'}`} data-testid={testId}>
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-sm font-medium">{label}</span>
                                      {isApproved && <Badge className="bg-green-500/20 text-green-500 border-green-500/50 text-[10px]">Approuvé</Badge>}
                                      {isPending && <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50 text-[10px]">En attente</Badge>}
                                      {isRejected && <Badge className="bg-red-500/20 text-red-500 border-red-500/50 text-[10px]">Refusé</Badge>}
                                    </div>
                                    {uploaded && (
                                      <p className="text-xs text-muted-foreground mb-2 truncate">{uploaded.file_name}</p>
                                    )}
                                    {isRejected && uploaded.rejection_reason && (
                                      <p className="text-xs text-red-500 mb-2">Raison : {uploaded.rejection_reason}</p>
                                    )}
                                    {(isMissing || isRejected) && (
                                      <Input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        className="text-xs h-8"
                                        disabled={uploadingKyc}
                                        data-testid={`${testId}-upload`}
                                        onChange={async (e) => {
                                          const file = e.target.files?.[0];
                                          if (!file) return;
                                          e.target.value = '';
                                          try {
                                            setUploadingKyc(true);
                                            const reader = new FileReader();
                                            reader.onloadend = async () => {
                                              await axios.post(`${API}/kyc/upload`, {
                                                document_type: type.value,
                                                file_url: reader.result,
                                                file_name: file.name
                                              }, { headers: getHeaders(token) });
                                              toast.success(`${label} téléversé`);
                                              fetchData();
                                              setUploadingKyc(false);
                                            };
                                            reader.readAsDataURL(file);
                                          } catch {
                                            toast.error('Erreur téléversément');
                                            setUploadingKyc(false);
                                          }
                                        }}
                                      />
                                    )}
                                    {isMissing && !isRejected && (
                                      <p className="text-xs text-muted-foreground mt-1">PDF, JPG, PNG</p>
                                    )}
                                  </div>
                                );
                              })}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  }

                  // Render other document types normally
                  otherTypes.forEach(docType => {
                    const uploaded = kycDocs.find(d => d.document_type === docType.value);
                    const isApproved = uploaded?.status === 'approved';
                    const isRejected = uploaded?.status === 'rejected';
                    const isPending = uploaded && !isApproved && !isRejected;
                    const isMissing = !uploaded;

                    elements.push(
                      <Card key={docType.value} className={`card-premium ${isApproved ? 'border-green-500/30' : isRejected ? 'border-red-500/30' : isPending ? 'border-yellow-500/30' : 'border-dashed'}`} data-testid={`kyc-doc-${docType.value}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isApproved ? 'bg-green-500/20' : isRejected ? 'bg-red-500/20' : isPending ? 'bg-yellow-500/20' : 'bg-muted'}`}>
                                {isApproved ? <CheckCircle className="w-5 h-5 text-green-500" /> :
                                 isRejected ? <XCircle className="w-5 h-5 text-red-500" /> :
                                 isPending ? <Clock className="w-5 h-5 text-yellow-500" /> :
                                 <FileText className="w-5 h-5 text-muted-foreground" />}
                              </div>
                              <div>
                                <p className="font-semibold">{docType.label}</p>
                                {uploaded && <p className="text-xs text-muted-foreground">{uploaded.file_name} — {new Date(uploaded.uploaded_at || uploaded.created_at).toLocaleDateString('fr-FR')}</p>}
                                {isMissing && <p className="text-xs text-muted-foreground">Document non fourni</p>}
                                {isRejected && uploaded.rejection_reason && <p className="text-xs text-red-500 mt-0.5">Raison : {uploaded.rejection_reason}</p>}
                              </div>
                            </div>
                            <div>
                              {isApproved && <Badge className="bg-green-500/20 text-green-500 border-green-500/50">Approuvé</Badge>}
                              {isPending && <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50">En attente</Badge>}
                              {isRejected && <Badge className="bg-red-500/20 text-red-500 border-red-500/50">Refusé</Badge>}
                            </div>
                          </div>
                          {(isMissing || isRejected) && (
                            <div className="mt-3 flex items-center gap-2">
                              <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                className="flex-1 text-xs h-8"
                                disabled={uploadingKyc}
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  e.target.value = '';
                                  try {
                                    setUploadingKyc(true);
                                    const reader = new FileReader();
                                    reader.onloadend = async () => {
                                      await axios.post(`${API}/kyc/upload`, {
                                        document_type: docType.value,
                                        file_url: reader.result,
                                        file_name: file.name
                                      }, { headers: getHeaders(token) });
                                      toast.success(`${docType.label} téléversé`);
                                      fetchData();
                                      setUploadingKyc(false);
                                    };
                                    reader.readAsDataURL(file);
                                  } catch {
                                    toast.error('Erreur téléversément');
                                    setUploadingKyc(false);
                                  }
                                }}
                              />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">PDF, JPG, PNG</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  });

                  return elements;
                })()}
              </div>
            );
          })()}
        </div>

        {/* Sign Advance Dialog */}
        {signAdvance && (
          <Dialog open={!!signAdvance} onOpenChange={() => setSignAdvance(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Signer le contrat d'avance de fonds</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-sm text-muted-foreground">Montant de l'avance</p>
                  <p className="text-2xl font-bold">{signAdvance.amount?.toLocaleString('fr-FR')} €</p>
                  <p className="text-xs text-muted-foreground">Ref: AVF-{signAdvance.id?.slice(0, 8).toUpperCase()}</p>
                  {signAdvance.due_date && <p className="text-xs text-muted-foreground mt-1">Remboursement avant le : {signAdvance.due_date}</p>}
                  {signAdvance.penalty_rate > 0 && <p className="text-xs text-red-400 mt-1">Pénalités de retard : {signAdvance.penalty_rate}% / {({day:'jour',week:'semaine',month:'mois'})[signAdvance.penalty_period] || 'mois'}</p>}
                </div>
                <div>
                  <Label>Ville *</Label>
                  <Input value={signCity} onChange={e => setSignCity(e.target.value)} placeholder="Paris" data-testid="sign-city" />
                </div>
                <div>
                  <SignaturePad ref={signPadRef} width={400} height={120} label="Votre signature" penColor="#1a365d" backgroundColor="#ffffff" data-testid="advance-signature-pad" />
                </div>
                <div className="space-y-3 pt-2 border-t border-border/30">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={signAccord} onChange={e => setSignAccord(e.target.checked)} className="mt-1" />
                    <span className="text-sm">Bon pour accord — Je reconnais avoir pris connaissance des conditions de cette avance de fonds</span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={signTerms} onChange={e => setSignTerms(e.target.checked)} className="mt-1" />
                    <span className="text-sm">J'accepte les termes et conditions du contrat d'avance de fonds</span>
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSignAdvance(null)}>Annuler</Button>
                <Button disabled={!signCity || !signAccord || !signTerms || signing} onClick={async () => {
                  const sigData = signPadRef.current?.getDataURL();
                  if (!sigData || signPadRef.current?.isEmpty()) {
                    toast.error('Veuillez signer dans le cadre');
                    return;
                  }
                  setSigning(true);
                  try {
                    await axios.put(`${API}/advances/${signAdvance.id}`, {
                      city: signCity,
                      signature: sigData,
                    }, { headers: getHeaders(token) });
                    toast.success('Contrat signé avec succès !');
                    setSignAdvance(null);
                    fetchData();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                  finally { setSigning(false); }
                }} data-testid="confirm-sign-advance">
                  {signing ? 'Signature en cours...' : 'Signer le contrat'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Sign Mandate Dialog */}
        {signMandate && (
          <Dialog open={!!signMandate} onOpenChange={() => setSignMandate(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Signer le mandat de gestion</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-secondary/30 rounded-lg">
                  <p className="text-lg font-bold">Mandat de Gestion</p>
                  <p className="text-xs text-muted-foreground">Ref: MDG-{signMandate.id?.slice(0,8).toUpperCase()}</p>
                </div>
                <div><Label>Ville *</Label><Input value={signCity} onChange={e => setSignCity(e.target.value)} placeholder="Paris" /></div>
                <div><SignaturePad ref={signPadRef} width={400} height={120} label="Votre signature" penColor="#1a365d" backgroundColor="#ffffff" /></div>
                <div className="space-y-3 pt-2 border-t border-border/30">
                  <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" checked={signAccord} onChange={e => setSignAccord(e.target.checked)} className="mt-1" /><span className="text-sm">Bon pour accord — Je reconnais avoir pris connaissance des conditions du mandat de gestion</span></label>
                  <label className="flex items-start gap-3 cursor-pointer"><input type="checkbox" checked={signTerms} onChange={e => setSignTerms(e.target.checked)} className="mt-1" /><span className="text-sm">J'accepte les termes et conditions du mandat de gestion</span></label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSignMandate(null)}>Annuler</Button>
                <Button disabled={!signCity || !signAccord || !signTerms || signing} onClick={async () => {
                  const sigData = signPadRef.current?.getDataURL();
                  if (!sigData || signPadRef.current?.isEmpty()) { toast.error('Veuillez signer'); return; }
                  setSigning(true);
                  try {
                    await axios.put(`${API}/mandates/${signMandate.id}`, { city: signCity, signature: sigData }, { headers: getHeaders(token) });
                    toast.success('Mandat signé !'); setSignMandate(null); fetchData();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                  finally { setSigning(false); }
                }}>
                  {signing ? 'Signature...' : 'Signer le mandat'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
}
